import { exists } from '@jack-henry/frontend-utils/functions';
import { AccountModelDto } from '@treasury/api/bo';
import { hashString } from '@treasury/utils/functions';

/**
 * The properties needed to generate a unique ID for an account-like object.
 */
type UniqueAccountProps = Pick<
    AccountModelDto,
    'id' | 'companyName' | 'number' | 'cifNumber' | 'type'
>;

/**
 * Accounts do not always have their `id` property filled in
 * but we need a way to identify entities that are the same
 * without relying on object reference comparisons.
 *
 * This generates an ID that can be used for comparisons
 * by hashing together unique properties of the account.
 */
export function createAccountId(account: UniqueAccountProps) {
    const { id, companyName, number: accountNumber, cifNumber, type: accountType } = account;
    if (exists(id) && id > 0) {
        return id;
    }

    const hashedParts = [companyName, accountNumber, cifNumber, accountType];

    if (!hashedParts.every(exists)) {
        throw new Error(
            'Could not create ID for account. It was missing one or more unique properties.'
        );
    }

    return hashString(hashedParts.join('-'));
}
